/*
 * Ledge predictive search — custom fetch layer.
 *
 * We hit Shopify's /search/suggest.json endpoint directly rather than
 * reusing Horizon's assets/predictive-search.js. Horizon's JS is tightly
 * coupled to its own DOM structure: it uses server-rendered HTML fetched
 * via sectionRenderer + morph(), and expects selectors like
 * `.predictive-search-results__card` and `[ref="resultsItems[]"]` in the
 * target container. Rewriting our overlay markup to mirror those classes
 * just to reuse the fetch/debounce logic would be more work — and more
 * coupling — than copying the ~6 lines of actual logic.
 *
 * This file re-implements debounce + AbortController cancellation on top
 * of native fetch and renders into our own markup with template literals.
 *
 * State machine:
 *   empty            → no query or < MIN_LENGTH chars
 *   loading          → request in flight (aria-busy=true)
 *   results          → at least one product / brand / story matched
 *   no-matches       → query ≥ MIN_LENGTH returned zero of everything
 *
 * Brand filter: Shopify's JSON API doesn't expose a collection's
 * `template_suffix`, so we can't filter server-side. Instead the overlay
 * section renders a space-separated allowlist of brand handles
 * (collections where template_suffix == "brand") into a data-attribute
 * at page render time. JS intersects the search response's collections
 * with that allowlist client-side.
 *
 * Accessibility (per liquid-theme-a11y skill):
 *   - aria-live="polite" on the results container → screen readers
 *     announce new results when they appear.
 *   - aria-busy toggled during fetch.
 *   - Focus enters the input on open, returns to the trigger on close.
 *   - `inert` on the panel when closed → full DOM subtree removed from
 *     tab order + accessibility tree (no manual focus trap needed).
 */
(function () {
  'use strict';

  var DEBOUNCE_MS = 150;
  var MIN_LENGTH = 2;
  var PRODUCT_LIMIT = 6;
  var BRAND_LIMIT = 3;
  var STORY_LIMIT = 3;
  var API_LIMIT = 10; // Shopify caps resources[limit] at 10 per type

  function escapeHtml(str) {
    if (str == null) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function readI18n(overlay) {
    var node = overlay.querySelector('[data-ledge-search-i18n]');
    if (!node) return {};
    try {
      return JSON.parse(node.textContent);
    } catch (_) {
      return {};
    }
  }

  function init(overlay) {
    if (overlay.dataset.ledgeInit === '1') return;
    overlay.dataset.ledgeInit = '1';

    var trigger = document.querySelector('[data-ledge-search-trigger]');
    var panel = overlay.querySelector('.ledge-search__overlay');
    var input = overlay.querySelector('[data-ledge-search-input]');
    var resultsEl = overlay.querySelector('[data-ledge-search-results]');
    var form = overlay.querySelector('[data-ledge-search-form]');
    var clearBtn = overlay.querySelector('[data-ledge-search-clear]');

    if (!panel || !input || !resultsEl) return;

    var endpoint = overlay.dataset.endpoint || '/search/suggest.json';
    var searchUrl = overlay.dataset.searchUrl || '/search';
    var brandsIndexUrl = overlay.dataset.brandsIndexUrl || '/pages/brands';
    var brandHandles = (overlay.dataset.brandHandles || '').split(/\s+/).filter(Boolean);
    var brandSet = new Set(brandHandles);
    var i18n = readI18n(overlay);

    var debounceTimer = null;
    var currentAbort = null;

    /**
     * Format a price (decimal string or number) using shop.money_format
     * when it's a simple {{amount}} template; otherwise fall back to
     * Intl.NumberFormat with the shop currency. Shopify's predictive
     * JSON returns price as a decimal string like "165.00" with no
     * currency symbol, so we have to format client-side.
     */
    function formatMoney(raw) {
      if (raw == null) return '';
      var num = typeof raw === 'string' ? parseFloat(raw) : Number(raw);
      if (!isFinite(num)) return String(raw);
      var format = i18n.moneyFormat || '';
      // Shopify money_format tokens: {{amount}}, {{amount_no_decimals}},
      // {{amount_with_comma_separator}}, {{amount_no_decimals_with_comma_separator}}.
      if (format.indexOf('{{amount') !== -1) {
        var withDecimals = num.toFixed(2);
        var withComma = withDecimals.replace('.', ',');
        var noDecimals = String(Math.round(num));
        var noDecimalsComma = noDecimals; // same digits, comma doesn't matter
        return format
          .replace('{{amount_no_decimals_with_comma_separator}}', noDecimalsComma)
          .replace('{{amount_with_comma_separator}}', withComma)
          .replace('{{amount_no_decimals}}', noDecimals)
          .replace('{{amount}}', withDecimals);
      }
      // Fall-through: Intl with shop currency
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency: i18n.currency || 'EUR',
        }).format(num);
      } catch (_) {
        return '€' + num.toFixed(2).replace('.', ',');
      }
    }

    function open() {
      overlay.setAttribute('data-open', 'true');
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
      panel.setAttribute('aria-hidden', 'false');
      panel.removeAttribute('inert');
      // Delay focus slightly so the fade-in animation doesn't fight the
      // browser's "focus triggers scroll" behaviour on mobile.
      window.setTimeout(function () {
        input.focus();
      }, 80);
    }

    function close() {
      overlay.setAttribute('data-open', 'false');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
      panel.setAttribute('aria-hidden', 'true');
      panel.setAttribute('inert', '');
      if (currentAbort) {
        currentAbort.abort();
        currentAbort = null;
      }
      if (debounceTimer) {
        window.clearTimeout(debounceTimer);
        debounceTimer = null;
      }
      // Reset to a clean slate for the next open
      input.value = '';
      if (clearBtn) clearBtn.setAttribute('hidden', '');
      showEmpty();
    }

    // Trigger
    if (trigger) {
      trigger.addEventListener('click', function (e) {
        e.preventDefault();
        if (overlay.getAttribute('data-open') === 'true') {
          close();
        } else {
          open();
        }
      });
    }

    // Close on backdrop / X (delegated)
    overlay.addEventListener('click', function (e) {
      if (e.target.closest('[data-ledge-search-close]')) {
        e.preventDefault();
        close();
        if (trigger) trigger.focus();
      }
    });

    // Escape
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      if (overlay.getAttribute('data-open') !== 'true') return;
      close();
      if (trigger) trigger.focus();
    });

    // Form submit → full search results page (Enter key without selecting a result)
    if (form) {
      form.addEventListener('submit', function () {
        // Let the native form submission proceed to /search?q=...
        close();
      });
    }

    // Clicking any result link closes the overlay; navigation proceeds natively.
    resultsEl.addEventListener('click', function (e) {
      var link = e.target.closest('a[data-ledge-search-result]');
      if (link) close();
    });

    function syncClearButton() {
      if (!clearBtn) return;
      if (input.value.length > 0) {
        clearBtn.removeAttribute('hidden');
      } else {
        clearBtn.setAttribute('hidden', '');
      }
    }

    // Debounced input
    input.addEventListener('input', function () {
      syncClearButton();
      var query = input.value.trim();
      if (debounceTimer) window.clearTimeout(debounceTimer);
      if (currentAbort) {
        currentAbort.abort();
        currentAbort = null;
      }
      if (query.length < MIN_LENGTH) {
        showEmpty();
        return;
      }
      resultsEl.setAttribute('aria-busy', 'true');
      debounceTimer = window.setTimeout(function () {
        fetchResults(query);
      }, DEBOUNCE_MS);
    });

    // Clear (×) button — wipe input, return focus, show empty state.
    if (clearBtn) {
      clearBtn.addEventListener('click', function (event) {
        event.preventDefault();
        input.value = '';
        syncClearButton();
        if (currentAbort) { currentAbort.abort(); currentAbort = null; }
        if (debounceTimer) { window.clearTimeout(debounceTimer); debounceTimer = null; }
        showEmpty();
        input.focus();
      });
    }

    function showEmpty() {
      resultsEl.setAttribute('aria-busy', 'false');
      resultsEl.innerHTML = '';
    }

    function fetchResults(query) {
      currentAbort = new AbortController();
      var url;
      try {
        url = new URL(endpoint, window.location.origin);
      } catch (_) {
        url = new URL('/search/suggest.json', window.location.origin);
      }
      url.searchParams.set('q', query);
      url.searchParams.set('resources[type]', 'product,collection,article');
      url.searchParams.set('resources[limit]', String(API_LIMIT));
      url.searchParams.set('resources[options][unavailable_products]', 'hide');

      fetch(url.toString(), {
        signal: currentAbort.signal,
        headers: { Accept: 'application/json' },
      })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          currentAbort = null;
          render(query, data);
        })
        .catch(function (err) {
          if (err && err.name === 'AbortError') return;
          // eslint-disable-next-line no-console
          console.warn('[ledge-search] fetch failed', err);
          resultsEl.setAttribute('aria-busy', 'false');
        });
    }

    function render(query, data) {
      resultsEl.setAttribute('aria-busy', 'false');
      var results = (data && data.resources && data.resources.results) || {};
      var products = (results.products || []).slice(0, PRODUCT_LIMIT);
      var allCollections = results.collections || [];
      var brands = allCollections
        .filter(function (c) {
          if (brandSet.size === 0) return true;
          return brandSet.has(c.handle);
        })
        .slice(0, BRAND_LIMIT);
      var stories = (results.articles || []).slice(0, STORY_LIMIT);

      if (products.length === 0 && brands.length === 0 && stories.length === 0) {
        renderNoMatches(query);
        return;
      }

      var html = [];
      html.push('<div class="ledge-search__grid">');
      if (products.length > 0) {
        html.push(
          '<div class="ledge-search__col ledge-search__col--products">',
          renderProducts(products, query, results.products.length),
          '</div>'
        );
      }
      if (brands.length > 0 || stories.length > 0) {
        html.push('<div class="ledge-search__col ledge-search__col--meta">');
        if (brands.length > 0) html.push(renderBrands(brands));
        if (stories.length > 0) html.push(renderStories(stories));
        html.push('</div>');
      }
      html.push('</div>');
      resultsEl.innerHTML = html.join('');
    }

    function renderProducts(list, query, returnedCount) {
      var items = list
        .map(function (p) {
          var img =
            (p.featured_image && p.featured_image.url) ||
            (p.image && (p.image.url || p.image)) ||
            '';
          // Downsize: swap _original / raw URL for a 400 width variant
          // Shopify image CDN supports width in path segment; crude but works
          if (img && img.indexOf('width=') === -1 && img.indexOf('_') !== -1) {
            img = img.replace(/(\.(jpe?g|png|webp|gif))(\?.*)?$/i, '_400x$1$3');
          }
          var priceHtml = '';
          if (p.price != null) {
            priceHtml = escapeHtml(formatMoney(p.price));
          }
          return (
            '<li class="ledge-search__product">' +
              '<a class="ledge-search__product-link" href="' + escapeHtml(p.url) + '" data-ledge-search-result>' +
                '<div class="ledge-search__product-thumb">' +
                  (img
                    ? '<img loading="lazy" alt="" src="' + escapeHtml(img) + '" />'
                    : '<div class="ledge-search__product-thumb-placeholder" aria-hidden="true"></div>') +
                '</div>' +
                '<div class="ledge-search__product-info">' +
                  (p.vendor
                    ? '<span class="ledge-search__product-vendor">' + escapeHtml(p.vendor) + '</span>'
                    : '') +
                  '<span class="ledge-search__product-title">' + escapeHtml(p.title || '') + '</span>' +
                  (priceHtml
                    ? '<span class="ledge-search__product-price">' + priceHtml + '</span>'
                    : '') +
                '</div>' +
              '</a>' +
            '</li>'
          );
        })
        .join('');

      var viewAll = '';
      if (returnedCount > PRODUCT_LIMIT || returnedCount === API_LIMIT) {
        var viewAllUrl = searchUrl + '?q=' + encodeURIComponent(query) + '&type=product';
        viewAll =
          '<a class="ledge-search__view-all" href="' + escapeHtml(viewAllUrl) + '" data-ledge-search-result>' +
            escapeHtml(i18n.viewAllResults || 'View all results →') +
          '</a>';
      }

      return (
        '<h3 class="ledge-search__section-label">' + escapeHtml(i18n.products || 'Products') + '</h3>' +
        '<ul class="ledge-search__product-grid" role="list">' + items + '</ul>' +
        viewAll
      );
    }

    function renderBrands(list) {
      var items = list
        .map(function (b) {
          var desc = '';
          if (b.body) desc = b.body;
          else if (b.description) desc = b.description;
          desc = desc.replace(/<[^>]*>/g, '').trim();
          return (
            '<li class="ledge-search__brand">' +
              '<a class="ledge-search__brand-link" href="' + escapeHtml(b.url) + '" data-ledge-search-result>' +
                '<span class="ledge-search__brand-name">' + escapeHtml(b.title || '') + '</span>' +
                (desc
                  ? '<span class="ledge-search__brand-desc">' + escapeHtml(desc) + '</span>'
                  : '') +
              '</a>' +
            '</li>'
          );
        })
        .join('');
      return (
        '<div class="ledge-search__section ledge-search__section--brands">' +
          '<h3 class="ledge-search__section-label">' + escapeHtml(i18n.brands || 'Brands') + '</h3>' +
          '<ul class="ledge-search__brand-list" role="list">' + items + '</ul>' +
        '</div>'
      );
    }

    function renderStories(list) {
      var items = list
        .map(function (s) {
          // Shopify's JSON articles endpoint doesn't expose author/brand
          // directly; the "by {blog/brand}" label uses the blog title as
          // a reasonable fallback. If a richer source becomes available
          // we can swap it in without touching markup.
          var attribution = s.blog && s.blog.title ? s.blog.title : '';
          return (
            '<li class="ledge-search__story">' +
              '<a class="ledge-search__story-link" href="' + escapeHtml(s.url) + '" data-ledge-search-result>' +
                '<span class="ledge-search__story-title">' + escapeHtml(s.title || '') + '</span>' +
                (attribution
                  ? '<span class="ledge-search__story-attr">' +
                      escapeHtml((i18n.byLabel || 'by') + ' ' + attribution) +
                    '</span>'
                  : '') +
              '</a>' +
            '</li>'
          );
        })
        .join('');
      return (
        '<div class="ledge-search__section ledge-search__section--stories">' +
          '<h3 class="ledge-search__section-label">' + escapeHtml(i18n.stories || 'Stories') + '</h3>' +
          '<ul class="ledge-search__story-list" role="list">' + items + '</ul>' +
        '</div>'
      );
    }

    function renderNoMatches(query) {
      var prefix = i18n.noMatchesPrefix || 'No matches for';
      var browse = i18n.browseInstead || 'Try a different search or browse our brands';
      var linkLabel = i18n.browseLinkLabel || 'browse our brands';
      // Put the link where "browse our brands" appears in the helper
      // string. If the i18n value doesn't contain that substring (e.g.
      // a German translation rephrases), fall back to appending a plain
      // "→" link.
      var browseHtml;
      if (browse.indexOf(linkLabel) !== -1) {
        browseHtml = escapeHtml(browse).replace(
          escapeHtml(linkLabel),
          '<a class="ledge-search__browse-link" href="' + escapeHtml(brandsIndexUrl) + '" data-ledge-search-result>' + escapeHtml(linkLabel) + ' →</a>'
        );
      } else {
        browseHtml =
          escapeHtml(browse) +
          ' <a class="ledge-search__browse-link" href="' + escapeHtml(brandsIndexUrl) + '" data-ledge-search-result>→</a>';
      }
      resultsEl.innerHTML =
        '<div class="ledge-search__no-matches">' +
          '<p class="ledge-search__no-matches-title">' +
            escapeHtml(prefix) + ' &ldquo;' + escapeHtml(query) + '&rdquo;' +
          '</p>' +
          '<p class="ledge-search__no-matches-helper">' + browseHtml + '</p>' +
        '</div>';
    }

    showEmpty();
  }

  function boot() {
    document.querySelectorAll('ledge-search-overlay').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
